import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";
import TryItButton from "../../../TryItButton";
import OutputBox from "../../../OutputBox";

export default function JS_Loops_while() {
  return (
    <div className="tutorial-container read-only">
      <h1 className="tutorial-title">🔁 JavaScript while Loop</h1>

      {/* Explanation */}
      <div className="tutorial-section">
        <h2 className="section-title">💡 What is a while loop?</h2>
        <p>
          The <code className="highlight_code">while</code> loop executes a
          block of code repeatedly **as long as a specified condition is true**.
          It's ideal when you don't know beforehand how many times you need to
          repeat the task.
        </p>
        <div
          style={{
            background: "linear-gradient(135deg, #fef3c7, #fde68a)",
            padding: "1rem",
            borderRadius: "12px",
            marginTop: "2rem",
            paddingLeft: "35px",
          }}
        >
          {/* How it works */}
          <h3 style={{ color: "#92400e" }}>🛠 How it works:</h3>
          <ol className="tutorial-list-points">
            <li>
              <b>Initialization:</b> You first set a variable before the loop
              starts, e.g., <code className="highlight_code ">let i = 1;</code>.
            </li>
            <li>
              <b>Condition:</b> The loop keeps running{" "}
              <b>while the condition is true</b>, e.g.,{" "}
              <code className="highlight_code ">i &lt;= 5</code>.
            </li>
            <li>
              <b>Code Block:</b> The statements inside the curly braces{" "}
              <code className="highlight_code ">{`{ ... }`}</code> are executed
              each time.
            </li>
            <li>
              <b>Increment/Update:</b> Update your variable inside the loop to
              eventually make the condition false, e.g.,{" "}
              <code className="highlight_code ">i++</code>. Without this, the
              loop could run forever (infinite loop).
            </li>
          </ol>
        </div>
        <p>Example Flow:</p>
        <pre>
          Initialization → Check Condition → Execute Code → Increment/Update →
          Check Condition → … Repeat
        </pre>

        <p>
          At <b>Edu-Tech Master Academy</b>, understanding this flow is
          essential to control loop execution safely.
        </p>
      </div>

      {/* Example 1 */}
      <div className="tutorial-section">
        <h2 className="section-title">1️⃣ Counting Numbers</h2>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`let i = 1;  // Initialization
while (i <= 5) { // Condition
  console.log("Count:", i); // Code Block
  i++; // Increment
}`}
          />
          <TryItButton
            code={`let i = 1;
while (i <= 5) {
  console.log("Count:", i);
  i++;
}`}
          />
        </div>
        <OutputBox>
          <pre>
            Count: 1{"\n"}
            Count: 2{"\n"}
            Count: 3{"\n"}
            Count: 4{"\n"}
            Count: 5
          </pre>
        </OutputBox>
      </div>

      {/* Example 2 */}
      <div className="tutorial-section">
        <h2 className="section-title">2️⃣ Sum of Numbers</h2>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`let sum = 0; // Initialization
let num = 1;
while (num <= 5) { // Condition
  sum += num; // Code Block
  num++; // Increment
}
console.log("Sum at Edu-Tech Master Academy:", sum);`}
          />
          <TryItButton
            code={`let sum = 0;
let num = 1;
while (num <= 5) {
  sum += num;
  num++;
}
console.log("Sum at Edu-Tech Master Academy:", sum);`}
          />
        </div>
        <OutputBox>
          <pre>Sum at Edu-Tech Master Academy: 15</pre>
        </OutputBox>
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #dbeafe, #bfdbfe)",
          padding: "1rem",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#1e40af" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Compare <code className="highlight_code ">while</code> with{" "}
            <code className="highlight_code ">for</code> loops and understand
            differences in initialization and increment.
          </li>
          <li>
            Practice using <code className="highlight_code ">break</code> and{" "}
            <code className="highlight_code ">continue</code> statements inside
            loops.
          </li>
          <li>Experiment with loops over arrays, objects, and iterables.</li>
        </ul>
      </div>
    </div>
  );
}
